<template>
  <div :id="chartId" style="height: 300px; width: 100%;" />
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import * as echarts from 'echarts';

import { Nullable } from '@/utils/types';
import { IGroupedBarChartData } from '@/bundles/Visualizations/types';

interface ITooltipParam {
  seriesIndex: number;
  seriesName: string;
  value: number | null;
  marker: string;
  axisValueLabel: string;
}

interface IEChartBarLabelRotation {
  myChart: Nullable<echarts.ECharts>;
}

export default Vue.extend({
  name: 'EChartBarLabelRotation',

  props: {
    chartId: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    chartData: {
      type: Object as PropType<IGroupedBarChartData>,
      required: true
    },
  },

  data: (): IEChartBarLabelRotation => ({
    myChart: null,
  }),

  watch: {
    chartData: {
      handler () {
        this.loadChart();
      },
      deep: true,
    }
  },

  mounted () {
    this.loadChart();
  },

  methods: {
    formatTooltipValue (param: ITooltipParam): string {
      const seriesConfig = this.chartData.series[param.seriesIndex];
      const valueFormatter = seriesConfig?.tooltip?.valueFormatter;

      return valueFormatter ? valueFormatter(param.value as number) : String(param.value);
    },

    formatTooltip (params: ITooltipParam[]): string {
      const items = params
        .filter(p => p.value != null)
        .sort((a, b) => b.value! - a.value!);

      if (!items.length) return '';

      const header = items[0].axisValueLabel;
      const rows = items.map(p => `${p.marker}${p.seriesName} <b>${this.formatTooltipValue(p)}</b>`);

      return [header, ...rows].join('<br/>');
    },

    loadChart () {
      const container = document.getElementById(this.chartId);

      if (!container) {
        return;
      }

      if (!this.myChart) {
        this.myChart = echarts.init(container);
      }

      this.myChart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: this.formatTooltip
        },
        legend: {
          data: this.chartData.series.map(s => s.name),
          type: 'scroll',
          bottom: 0,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          axisTick: { show: false },
          data: this.chartData.categories,
        },
        yAxis: {
          type: 'value',
        },
        series: this.chartData.series,
      },
      true
      );
    }
  }
})
</script>
